import {
   CaptchaGateScope,
   FeatureToggleAppId,
   ResolveCaptchaGateScopeArgs
} from '../types/CaptchaGateTypes'

/**
 * Resolve the captcha gate scope from compile-time app identity plus runtime
 * portal signals. Rows are evaluated top-down, first match wins. The scope
 * mirrors the pre-migration `.env` builds that could independently toggle
 * captcha, so each app/portal reads its own gate value.
 *
 * Any unresolvable combination returns `Unknown` -> no matching gate exists
 * -> `:Unrecognized` -> fail-secure ON.
 */
export const resolveCaptchaGateScope = (
   args: ResolveCaptchaGateScopeArgs
): CaptchaGateScope => {
   const { appId, clientEnum, portalType, portalSubType } = args

   // Non-portal apps are keyed purely on the compile-time appId. Officer and
   // admin share the `MA&UD_ADMIN` client enum, so only appId separates them.
   switch (appId) {
      case FeatureToggleAppId.BpsOfficer:
         return CaptchaGateScope.Officer
      case FeatureToggleAppId.Admin:
         return CaptchaGateScope.Admin
      case FeatureToggleAppId.Crm:
         return CaptchaGateScope.Crm
      case FeatureToggleAppId.Portals:
         break
      default:
         return CaptchaGateScope.Unknown
   }

   // Portal sub-type discriminates the specialised portals-web builds first;
   // these are set regardless of client enum.
   switch (portalSubType) {
      case 'LTP':
         return CaptchaGateScope.Ltp
      case 'TDR_OPEN_SEARCH':
         return CaptchaGateScope.OpenTdr
      case 'TDR_BANK':
         return CaptchaGateScope.TdrBank
      case 'DIGITALIZATION_PORTAL':
         return CaptchaGateScope.DigiPortal
      default:
         break
   }

   // No specialised sub-type: distinguish the TGIIC citizen build, then the
   // remaining architect / citizen builds by portal type.
   const hasBaseSubType =
      portalSubType === undefined ||
      portalSubType === '' ||
      portalSubType === 'DEFAULT'

   if (
      clientEnum === 'TGIIC' &&
      hasBaseSubType &&
      portalType === 'CITIZEN_PORTAL'
   ) {
      return CaptchaGateScope.TgiicClu
   }

   if (hasBaseSubType && portalType === 'ARCHITECT_PORTAL') {
      return CaptchaGateScope.Architect
   }

   if (hasBaseSubType && portalType === 'CITIZEN_PORTAL') {
      return CaptchaGateScope.Citizen
   }

   return CaptchaGateScope.Unknown
}

/**
 * Compose the scope-suffixed gate name, e.g.
 * `enable_captcha_login` + `officer` -> `enable_captcha_login_officer`.
 * A `null` scope (consumer rendered outside the provider) maps to `unknown`,
 * which has no gate and therefore fails secure (captcha ON).
 */
export const getCaptchaGateName = (
   featureGate: string,
   scope: CaptchaGateScope | null
): string => `${featureGate}_${scope ?? CaptchaGateScope.Unknown}`
